import React from "react";
import { useTheme } from "react-jss";
import classnames from "classnames";
import useStyles from "./ChoiceListRenderer.styles";
import { RendererProps, getRendererProps } from "../BaseRenderer";
import { ChoiceModel } from "../../../models/ChoiceModel";
import { QuestionType } from "../../../models/enums/QuestionType";
import ChoiceListItemRenderer from "./ChoiceListItemRenderer";
import { MentionModel } from "../../../models/MentionModel";
import { CheckBox } from "../../choices/CheckBox";
import { Radio } from "../../choices/Radio";
import { ImageSelect } from "../../choices/ImageSelect";
import {
    getSettingBoolean,
    getSettingNumber,
    getSettingString,
} from "../../../models/utilities/settingUtilities";
import { VariableModel } from "../../../models/VariableModel";
import Button from "../../shared/Button";
import useQuestion from "../../../hooks/useQuestion";
import useTranslatable from "../../../hooks/useTranslatable";
import { QuestionModel } from "../../../models/QuestionModel";
import { getChoiceListDisplayType } from "../../../models/utilities/questionUtilities";
import { ChoiceListDisplayType } from "../../../models/enums/ChoiceListDisplayType";
import useDimensionContext from "../../../hooks/useDimensionContext";
import { isIE } from "../../../utils";

interface Props extends RendererProps {
    variable: VariableModel;
    openEndValue?: { [key: string]: string };
    onClick?(choice: ChoiceModel): void;
    onOpenEndChange?(choice: ChoiceModel, text: string): void;
    onOpenEndBlur?(choice: ChoiceModel): void;
    mentions: MentionModel[];
    choices: ChoiceModel[];
    gridRendering?: boolean;
    onReset(): void;
    language: string;
    maxAnswers?: number;
    isSelected?: boolean;
    isCarrouselCheckboxActive?: boolean;
    onAfterSelection?(): void;
    executeUpdateForIdAfterSelection?: string;
}

const shouldDisplayResetButton = (question: QuestionModel) =>
    getSettingBoolean(question, "DisplayResetButton");

function isSelected(props: Props, choice: ChoiceModel) {
    const { mentions } = props;
    return mentions.some((mention) => mention.value === choice.value);
}

function isChoiceDisabled(props: Props, selected: boolean, controlType: any) {
    const { question, maxAnswers, mentions } = props;
    if (controlType === Radio) {
        return false;
    }
    if (maxAnswers === -1) {
        return false;
    }
    if (question.type === QuestionType.ImageSelector && maxAnswers === 1) {
        return false;
    }
    return !selected && mentions.length >= maxAnswers;
}

function renderChoiceListItem(
    props: Props,
    choice: ChoiceModel,
    isFirst: boolean,
    isLast: boolean,
    key: any,
    controlType: any,
) {
    const {
        question,
        gridRendering,
        openEndValue,
        onOpenEndChange,
        onOpenEndBlur,
        variable,
        onAfterSelection,
        executeUpdateForIdAfterSelection,
    } = props;
    const displayType = getChoiceListDisplayType(question);
    const isHorizontalDisplay =
        displayType === ChoiceListDisplayType.Horizontal ||
        displayType === ChoiceListDisplayType.HorizontalColumns;
    const selected = isSelected(props, choice);
    const isDisabled = isChoiceDisabled(props, selected, controlType);
    return (
        <ChoiceListItemRenderer
            key={key}
            {...getRendererProps(props)}
            controlType={controlType}
            gridRendering={gridRendering}
            isFirst={isFirst}
            isLast={isLast}
            openEnd={(openEndValue || {})[choice.index]}
            onOpenEndChange={onOpenEndChange}
            onOpenEndBlur={onOpenEndBlur}
            selected={selected}
            choice={choice}
            variable={variable}
            disabled={isDisabled}
            horizontal={isHorizontalDisplay}
            onAfterSelection={onAfterSelection}
            executeUpdateForIdAfterSelection={executeUpdateForIdAfterSelection}
        />
    );
}

function renderChoice(
    props: Props,
    choice: ChoiceModel,
    isFirst: boolean,
    isLast: boolean,
    key: any,
) {
    const { question, isCarrouselCheckboxActive } = props;
    switch (question.type) {
        case QuestionType.ImageSelector:
            return renderChoiceListItem(props, choice, isFirst, isLast, key, ImageSelect);
        case QuestionType.CheckBox:
            return renderChoiceListItem(props, choice, isFirst, isLast, key, CheckBox);
        case QuestionType.Carrousel:
            return renderChoiceListItem(
                props,
                choice,
                isFirst,
                isLast,
                key,
                isCarrouselCheckboxActive ? CheckBox : Radio,
            );
        default:
            return renderChoiceListItem(props, choice, isFirst, isLast, key, Radio);
    }
}

function renderChoices(props: Props) {
    const { choices } = props;
    return choices.map((choice, i) =>
        renderChoice(props, choice, i === 0, i === choices.length - 1, choice.name),
    );
}

function ResetButton(props: Props) {
    const { theme, onReset } = props;
    const question = useQuestion();
    const defaultClearButtonText = useTranslatable(theme, "QuestionClearButton") || "Reset";
    const clearButtonText = useTranslatable(question, "ResetButtonText") || defaultClearButtonText;

    return (
        <Button
            className="button--clear"
            text={clearButtonText}
            size="full"
            onClick={() => onReset()}
        />
    );
}

export default function ChoiceListRenderer(props: Props) {
    const question = useQuestion();
    const { choices, isCarrouselCheckboxActive } = props;

    const dimension = useDimensionContext();

    const theme = useTheme();
    const displayType = getChoiceListDisplayType(question);

    const numberOfColumns = getSettingNumber(question, "NumberOfColumns");
    const numberOfRows = numberOfColumns > 1 ? Math.ceil(choices?.length / numberOfColumns) : 1;
    const classes = useStyles({ theme, displayType, numberOfColumns, numberOfRows });

    if (choices === undefined || choices.length === 0) {
        return null;
    }

    const displayAsTable = question.type === QuestionType.ImageSelector;
    const displayOrder = getSettingString(question, "DisplayOrder");

    const isDisplayColumns = displayType === ChoiceListDisplayType.VerticalColumns;
    const isDisplayWide = dimension && dimension.minWidth(778);

    // Internet Explorer fallback layout: vertical columns choice list will be displayed as a simple vertical choice list
    const displayAsGrid = isDisplayColumns && isDisplayWide && !isIE();

    const role = question.type === QuestionType.RadioButton ? "radiogroup" : "group";

    return (
        <div
            role={role}
            className={classnames(
                classes.choiceList,
                displayAsTable && displayOrder === "Vertical" && classes.choiceListTableVertical,
                displayAsTable &&
                    displayOrder === "Horizontal" &&
                    classes.choiceListTableHorizontal,
                displayAsGrid && classes.choiceListGrid,
                isCarrouselCheckboxActive && classes.checkBoxAlignment,
            )}
        >
            {renderChoices(props)}
            {shouldDisplayResetButton(question) && <ResetButton {...props} />}
        </div>
    );
}
